import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageSquareText, ShieldCheck } from "lucide-react";
import { business, services, serviceRegions } from "@/data/site";
import { GoogleG } from "@/components/common/GoogleBadge";

export const Footer = () => {
  const serviceAreas = useMemo(() => serviceRegions.flatMap((r) => r.areas), []);
  return (
    <footer className="bg-brand-deep text-white/80">
      <div className="mx-auto max-w-7xl py-16 container-px">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img src={business.logo} alt={`${business.legalName} logo`} className="h-14 w-14 rounded-lg object-cover ring-1 ring-white/15" />
              <div>
                <p
                  className="font-display text-2xl font-bold text-foreground"
                  style={{ WebkitTextStroke: "1px hsl(var(--primary))", paintOrder: "stroke fill" }}
                >
                  JRs Chair Repair
                </p>
                <p className="mt-1 text-xs font-medium uppercase tracking-[0.16em] text-white/55">Veteran Owned LLC</p>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-white/65">
              {business.tagline} &mdash; {business.subTagline}. Call or text with any issue or concern on any powered or non-powered mobility device.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1.5 text-sm font-medium text-white">
              <ShieldCheck className="h-4 w-4 text-accent" /> Proudly Veteran Owned
            </div>
            <p className="mt-4 text-sm text-white/55">
              {business.credentials} &middot; Based in {business.location} ({business.region})
            </p>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Services</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {services.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to="/services" className="text-white/65 transition-colors hover:text-primary">{s.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Service Area</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {serviceAreas.map((a) => (
                <li key={a} className="flex items-start gap-2 text-white/65">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary/80" /> {a}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Get in touch</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a href={business.phoneHref} className="flex items-center gap-3 text-white/80 transition-colors hover:text-primary">
                  <Phone className="h-4 w-4 text-primary" /> {business.phone}
                </a>
              </li>
              <li>
                <a href={business.smsHref} className="flex items-center gap-3 text-white/80 transition-colors hover:text-primary">
                  <MessageSquareText className="h-4 w-4 text-primary" /> Text us anytime
                </a>
              </li>
              <li>
                <a href={business.emailHref} className="flex items-center gap-3 break-all text-white/80 transition-colors hover:text-primary">
                  <Mail className="h-4 w-4 text-primary" /> {business.email}
                </a>
              </li>
              <li>
                <a href={business.google} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/80 transition-colors hover:text-primary">
                  <GoogleG className="h-4 w-4" /> Check us out on Google
                </a>
              </li>
            </ul>
            <p className="mt-5 text-sm text-white/55">Owner: {business.owner}</p>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-7 text-xs text-white/45 sm:flex-row">
          <p>&copy; {new Date().getFullYear()} {business.legalName}. All rights reserved.</p>
          <p className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-accent" /> {business.ribbon}
          </p>
        </div>
        <p className="mt-5 text-center text-[11px] leading-relaxed text-white/35">
          All product and company names, brands, and trademarks are the property of their respective owners.
          Their use here is for identification and descriptive purposes only and does not imply affiliation with or endorsement by {business.legalName}.
        </p>
      </div>
    </footer>
  );
};
